/**
 * ContentSwitcherPanel: Replaces the page-switcher dropdown in the builder top bar.
 * Opens as a floating panel (same style as BrandPanel) with tabs for Pages and Blog.
 * Future tabs: Events, Campaigns.
 */
import { useState } from "react"
import { Search, Trash2, X } from "lucide-react"
import { toast } from "sonner"
import type { PageSummary } from "@/stores/customizationStore"
import { BlogsTab } from "./BlogsTab"
import { CampaignsTab } from "./CampaignsTab"
import type { BlogLiveData } from "./BlogPostCanvas"
import type { CampaignLiveData } from "./CampaignCanvas"
import type { BlockType } from "./blocks/types"
import { FloatingPanel } from "./ui/FloatingPanel"
import { GROUP_LABEL, PANEL_INPUT } from "./ui/GroupLabel"

const PANEL_POSITION: React.CSSProperties = {
  top: 100,
  left: 12,
  width: "min(296px, calc(100vw - 24px))",
  maxHeight: "calc(100vh - 116px)",
  zIndex: 100,
}

// This panel's labels sit 1px tighter than the kit default (pixel parity)
const LABEL: React.CSSProperties = { ...GROUP_LABEL, marginBottom: 5 }

const INPUT = PANEL_INPUT

type Tab = "pages" | "blog" | "campaigns"

export interface ContentSwitcherPanelProps {
  // Pages tab
  pages: PageSummary[]
  pageId: string | null
  pageSlug: string | null
  onSelectPage: (page: PageSummary) => void
  onDeletePage: (page: PageSummary, e: React.MouseEvent) => void
  onAddPage: (slug: string, title: string, addToNav: boolean) => Promise<void>
  pageCreating: boolean
  // Blog tab
  orgId: string
  activeBlogPostId: string | null
  onSelectBlogPost: (postId: string | null) => void
  onBlogLiveUpdate: (data: BlogLiveData) => void
  primaryColor: string
  onClose: () => void
  onBlogBlockAdd?: (type: BlockType) => void
  activeCampaignId: string | null
  onSelectCampaign: (campaignId: string | null) => void
  onCampaignLiveUpdate: (data: CampaignLiveData) => void
  onCampaignBlockAdd?: (type: BlockType) => void
  // Initial tab
  initialTab?: Tab
}

export function ContentSwitcherPanel({
  pages,
  pageId,
  pageSlug,
  onSelectPage,
  onDeletePage,
  onAddPage,
  pageCreating,
  orgId,
  activeBlogPostId,
  onSelectBlogPost,
  onBlogLiveUpdate,
  primaryColor,
  onClose,
  onBlogBlockAdd,
  activeCampaignId,
  onSelectCampaign,
  onCampaignLiveUpdate,
  onCampaignBlockAdd,
  initialTab = "pages",
}: ContentSwitcherPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab)
  const [showAddPage, setShowAddPage] = useState(false)
  const [newSlug, setNewSlug] = useState("")
  const [newTitle, setNewTitle] = useState("")
  const [addToNav, setAddToNav] = useState(true)
  const [adding, setAdding] = useState(false)

  async function handleAddPageSubmit(e: React.FormEvent) {
    e.preventDefault()
    const slug = newSlug.trim().toLowerCase().replace(/\s+/g, "-") || "new-page"
    const title = newTitle.trim() || slug
    setAdding(true)
    try {
      await onAddPage(slug, title, addToNav)
      setShowAddPage(false)
      setNewSlug("")
      setNewTitle("")
      setAddToNav(true)
    } catch {
      toast.error("Failed to create page")
    } finally {
      setAdding(false)
    }
  }

  return (
    <FloatingPanel position={PANEL_POSITION} data-testid="content-switcher-panel">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px 0",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
          }}
        >
          Content
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.4)",
            fontSize: 16,
            padding: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Tab bar */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "10px 16px 0",
          flexShrink: 0,
        }}
      >
        {(["pages", "blog", "campaigns"] as Tab[]).map((tab) => {
          const labels = { pages: "Pages", blog: "Blog", campaigns: "Campaigns" }
          const active = activeTab === tab
          return (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveTab(tab)
                if (tab === "pages") {
                  onSelectBlogPost(null)
                  onSelectCampaign(null)
                }
                if (tab === "blog") {
                  onSelectCampaign(null)
                }
                if (tab === "campaigns") {
                  onSelectBlogPost(null)
                }
              }}
              style={{
                padding: "5px 12px",
                borderRadius: 7,
                background: active ? "rgba(255,255,255,0.1)" : "transparent",
                border: "none",
                color: active ? "#fff" : "rgba(255,255,255,0.4)",
                fontSize: 12,
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
                transition: "all 0.12s",
              }}
            >
              {labels[tab]}
            </button>
          )
        })}
      </div>

      {/* Divider */}
      <div
        style={{
          height: 1,
          background: "rgba(255,255,255,0.06)",
          margin: "10px 0 0",
          flexShrink: 0,
        }}
      />

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto" as const,
          minHeight: 0,
        }}
      >
        {activeTab === "pages" && (
          <PagesTab
            pages={pages}
            pageId={pageId}
            pageSlug={pageSlug}
            onSelectPage={(page) => {
              onSelectPage(page)
              onSelectBlogPost(null)
              onSelectCampaign(null)
            }}
            onDeletePage={onDeletePage}
            onAddPageClick={() => setShowAddPage(true)}
          />
        )}
        {activeTab === "blog" && (
          <BlogsTab
            orgId={orgId}
            activeBlogPostId={activeBlogPostId}
            onSelectBlogPost={onSelectBlogPost}
            onBlogLiveUpdate={onBlogLiveUpdate}
            onAddBlock={onBlogBlockAdd}
          />
        )}
        {activeTab === "campaigns" && (
          <CampaignsTab
            orgId={orgId}
            activeCampaignId={activeCampaignId}
            onSelectCampaign={onSelectCampaign}
            onCampaignLiveUpdate={onCampaignLiveUpdate}
            onAddBlock={onCampaignBlockAdd}
          />
        )}
      </div>

      {/* Add page inline form (shown when showAddPage) */}
      {showAddPage && activeTab === "pages" && (
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            padding: 16,
            flexShrink: 0,
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#fff",
              marginBottom: 12,
            }}
          >
            Add page
          </div>
          <form onSubmit={(e) => void handleAddPageSubmit(e)}>
            <div style={{ marginBottom: 10 }}>
              <label style={LABEL}>Slug (URL path)</label>
              <input
                type="text"
                placeholder="e.g. about-us"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                autoFocus
                style={{ ...INPUT, fontFamily: "monospace" }}
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={LABEL}>Title (optional)</label>
              <input
                type="text"
                placeholder="e.g. About Us"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                style={INPUT}
              />
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 14,
                cursor: "pointer",
                fontSize: 12,
                color: "rgba(255,255,255,0.5)",
              }}
            >
              <input
                type="checkbox"
                checked={addToNav}
                onChange={(e) => setAddToNav(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              Add to navigation
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddPage(false)
                  setNewSlug("")
                  setNewTitle("")
                  setAddToNav(true)
                }}
                style={{
                  flex: 1,
                  padding: "6px 10px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 7,
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={adding || pageCreating}
                style={{
                  flex: 1,
                  padding: "6px 10px",
                  background: adding || pageCreating ? "rgba(255,255,255,0.08)" : primaryColor,
                  border: "none",
                  borderRadius: 7,
                  color: adding || pageCreating ? "rgba(255,255,255,0.3)" : "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: adding || pageCreating ? "not-allowed" : "pointer",
                }}
              >
                {adding || pageCreating ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .content-page-item:hover { background: rgba(255,255,255,0.05) !important; }
        .content-page-item:hover .cp-delete { opacity: 1 !important; }
      `}</style>
    </FloatingPanel>
  )
}

// ─── PagesTab ────────────────────────────────────────────────────────────────

interface PagesTabProps {
  pages: PageSummary[]
  pageId: string | null
  pageSlug: string | null
  onSelectPage: (page: PageSummary) => void
  onDeletePage: (page: PageSummary, e: React.MouseEvent) => void
  onAddPageClick: () => void
}

function PagesTab({
  pages,
  pageId,
  pageSlug,
  onSelectPage,
  onDeletePage,
  onAddPageClick,
}: PagesTabProps) {
  const [search, setSearch] = useState("")

  const filteredPages = search
    ? pages.filter((p) =>
        (p.title ?? p.slug ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : pages

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Search */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ position: "relative" }}>
          <Search
            size={11}
            color="rgba(255,255,255,0.3)"
            style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            type="text"
            placeholder="Search pages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              padding: "7px 10px 7px 26px",
              color: "#fff",
              fontSize: 12,
              outline: "none",
              boxSizing: "border-box" as const,
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                padding: 2,
                cursor: "pointer",
                color: "rgba(255,255,255,0.3)",
                display: "flex",
              }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "0 0 4px" }} />

      {/* Page list */}
      <div style={{ padding: "2px 0" }}>
      {filteredPages.length === 0 && search ? (
        <div
          style={{
            padding: "20px 16px",
            color: "rgba(255,255,255,0.3)",
            fontSize: 12,
            textAlign: "center",
          }}
        >
          No pages match
        </div>
      ) : filteredPages.map((page) => {
        const isActive = page.id === pageId || page.slug === pageSlug
        return (
          <div
            key={page.id}
            className="content-page-item"
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              background: isActive ? "rgba(255,255,255,0.06)" : "transparent",
            }}
          >
            <button
              type="button"
              onClick={() => onSelectPage(page)}
              style={{
                flex: 1,
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                border: "none",
                color: isActive ? "#fff" : "rgba(255,255,255,0.6)",
                fontSize: 12,
                fontWeight: isActive ? 500 : 400,
                padding: 0,
              }}
            >
              <span>📄</span>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap" as const,
                }}
              >
                {page.title ?? page.slug ?? "Untitled"}
              </span>
            </button>
            {page.slug !== "home" && (
              <button
                type="button"
                onClick={(e) => onDeletePage(page, e)}
                aria-label="Delete page"
                className="cp-delete"
                style={{
                  padding: 4,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#ef4444",
                  opacity: 0,
                  transition: "opacity 0.15s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            )}
          </div>
        )
      })}
      </div>

      {/* Divider */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          margin: "4px 0",
        }}
      />

      {/* Add page */}
      <button
        type="button"
        data-testid="content-add-page"
        onClick={onAddPageClick}
        style={{
          padding: "8px 16px",
          borderRadius: 0,
          fontSize: 12,
          color: "rgba(255,255,255,0.35)",
          background: "transparent",
          border: "none",
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>＋</span>
        <span>Add page</span>
      </button>
    </div>
  )
}
